'use client';

import { IRedux } from '@/_store/types';
import { BRAND_NAMES } from '@/_utils/Constants';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import NumberToPrice from '@/_components/NumberToPrice';

interface BrandTabsProps {
  selectedShipperId: string;
  onSelectBrand: (shipperId: string) => void;
}

export default function BrandTabs({ selectedShipperId, onSelectBrand }: BrandTabsProps) {
  const cartState = useSelector((state: IRedux) => state.cart.cart);

  const brandsWithProducts = useMemo(() => {
    return Object.entries(cartState || {})
      .filter(([_, data]) => data?.products?.length > 0)
      .map(([shipperId, data]) => {
        const totalPrice = data.products.reduce((acc, product) => {
          let productTotal = (product.price_with_discount || product.price || 0) * product.quantity;
          if (product?.order_modifiers?.length > 0) {
            for (const modifier of product.order_modifiers) {
              productTotal += modifier.modifier_price * modifier.modifier_quantity * product.quantity;
            }
          }
          return acc + productTotal;
        }, 0);

        return {
          shipperId,
          brandName: BRAND_NAMES[shipperId] || 'Restaurant',
          totalPrice,
          productCount: data.products.length,
        };
      });
  }, [cartState]);

  if (!brandsWithProducts?.length) {
    return null;
  }

  return (
    <Box
      overflowX="auto"
      px={4}
      py={3}
      css={{
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      <Flex gap={2} w="max-content">
        {brandsWithProducts.map(({ shipperId, brandName, totalPrice }) => {
          const isSelected = selectedShipperId === shipperId;
          
          return (
            <Box
              key={shipperId}
              px={4}
              py={2}
              rounded={12}
              cursor="pointer"
              transition="all 0.2s"
              border="1px solid"
              borderColor={isSelected ? 'primary' : 'gray.200'}
              onClick={() => onSelectBrand(shipperId)}
              flexShrink={0}
            >
              <Text
                fontSize="sm"
                fontWeight={600}
                color={'text'}
                whiteSpace="nowrap"
              >
                {brandName}
              </Text>
              <NumberToPrice
                value={totalPrice}
                textProps={{
                  fontSize: 'xs',
                  color: '#A8A8A8',
                  fontWeight: 400,
                }}
                spanProps={{
                  fontSize: 'xs',
                  color: '#A8A8A8',
                  fontWeight: 400,
                }}
              />
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
}
